import { createRoot } from 'react-dom/client';
import { getCustomerDebtReport } from '../api/client';
import type { CustomerDebtDetail } from '../components/CustomerDetailsModal';
import PrintableReport, { PRINT_STYLES } from '../components/PrintableReport';
import type { TFunction } from 'i18next';
import type { i18n as I18nInstance } from 'i18next';

interface PrintCustomerReportOptions {
  customerId: number;
  customerName: string;
  /** Pre-fetched amounts for the summary header (from the documents table row). */
  invoicesAmount?: number;
  paidAmount?: number;
  openAmount?: number;
  t: TFunction;
  i18n: I18nInstance;
}

export async function printCustomerReport({
  customerId,
  customerName,
  invoicesAmount = 0,
  paidAmount = 0,
  openAmount = 0,
  t,
  i18n,
}: PrintCustomerReportOptions): Promise<void> {
  const res = await getCustomerDebtReport(customerId);
  const detail = res.data as CustomerDebtDetail;

  const detailsMap = new Map<number, CustomerDebtDetail>();
  detailsMap.set(customerId, detail);

  const dir = i18n.dir();
  const printWindow = window.open('', '_blank');
  if (!printWindow) return;

  const now = new Date().toISOString().slice(0, 10);

  printWindow.document.write(`<!DOCTYPE html>
<html lang="${i18n.language}" dir="${dir}">
<head>
  <meta charset="UTF-8" />
  <title>${t('reports.printTitle')} – ${customerName}</title>
  <style>${PRINT_STYLES}</style>
</head>
<body><div id="print-root"></div></body>
</html>`);
  printWindow.document.close();

  const container = printWindow.document.getElementById('print-root')!;
  const root = createRoot(container);
  root.render(
    <PrintableReport
      summary={{
        from_date: detail.from_date || now,
        to_date: detail.to_date || now,
        total_invoices_amount: detail.invoices_amount ?? invoicesAmount,
        total_receipts_amount: detail.paid_amount ?? paidAmount,
        total_unpaid_amount: detail.unpaid_amount ?? openAmount,
        total_amount: detail.invoices_amount ?? invoicesAmount,
        total_documents: detail.recent_documents?.length ?? 0,
      }}
      customers={[
        {
          customer_id: customerId,
          customer_name: detail.customer_name || customerName,
          invoices_count: detail.recent_documents?.filter((d) => d.document_category === 'invoice').length ?? 0,
          invoices_amount: detail.invoices_amount ?? invoicesAmount,
          receipts_count: detail.recent_documents?.filter((d) => d.document_category === 'receipt').length ?? 0,
          receipts_amount: detail.paid_amount ?? paidAmount,
          unpaid_amount: detail.unpaid_amount ?? openAmount,
          aged_debt_amount: detail.aged_debt_amount ?? 0,
          total_amount: detail.invoices_amount ?? invoicesAmount,
        },
      ]}
      customerDetails={detailsMap}
      debtAgeThresholdDays={detail.payment_terms_days || 60}
      t={t}
      locale={i18n.language}
      dir={dir as 'ltr' | 'rtl'}
    />
  );

  setTimeout(() => {
    printWindow.focus();
    printWindow.print();
  }, 500);
}
